import { encode, ParsedUrlQuery } from 'querystring'

import { LocaleType } from '@hhgtech/hhg-components/build/interfaces/types'
import {
  getURLwithSSOTracking,
  useSSOV2Store,
} from '@hhgtech/hhg-components/ssoV2'
import { GetServerSidePropsResult } from 'next'

import {
  IS_SSOV2_ENABLED,
  REDIRECTION_PAGES,
  SSO_URL,
  STATIC_PAGES,
} from 'interfaces/constants'
import type { RedirectionType, RedirectSourceType } from 'interfaces/types'

import { cleanUnicodeUrl } from './paths'

export const verifyNotFoundToRedirect = (
  slug: string | string[] | undefined,
  locale: LocaleType,
): GetServerSidePropsResult<unknown> => {
  // redirect pagination
  if (slug && slug?.length > 2 && slug?.slice(-2)) {
    const checkingSlug = slug?.slice(-2)
    if (
      checkingSlug[0] === 'page' &&
      parseInt(checkingSlug[1]) &&
      !isNaN(parseInt(checkingSlug[1]))
    ) {
      ;(slug as string[]).splice(-2, 2)
      ;(slug as string[]).push(`?${checkingSlug[0]}=${checkingSlug[1]}`)
      const redirectUrl = `/${(slug as string[]).join('/')}`

      return {
        redirect: {
          destination: cleanUnicodeUrl(redirectUrl),
          statusCode: 301,
        },
      }
    }
  }

  // redirect old static to new
  if (slug && slug?.length > 0 && slug?.slice(-1)) {
    const checkingSlug = slug?.slice(-1) as string

    const staticPage = Object.values(STATIC_PAGES[locale] || {})
      .filter(Boolean)
      .find((page) => {
        return (page as string).indexOf(checkingSlug) > -1
      })

    if (staticPage) {
      const redirectUrl = staticPage.startsWith('http')
        ? staticPage
        : `/static${staticPage}`

      return {
        redirect: {
          destination: cleanUnicodeUrl(redirectUrl),
          statusCode: 301,
        },
      }
    }
  }

  // custom redirect for each region
  if (slug) {
    const checkingSlug =
      slug.length > 0 ? `${(slug as string[]).join('/')}` : `${slug}`
    const redirectedPage = (REDIRECTION_PAGES[locale] || {})[
      Object.keys(REDIRECTION_PAGES[locale] || {}).find(
        (page) => page.search(checkingSlug) > -1,
      ) || 'none'
    ]

    if (redirectedPage) {
      return {
        redirect: {
          destination: cleanUnicodeUrl(redirectedPage),
          statusCode: 301,
        },
      }
    }
  }

  return {
    notFound: true,
  }
}

export const redirectToSSOWithReturnUrl = (
  url?: string,
  source?: RedirectSourceType,
  action?: string,
  campaign?: string,
) => {
  const originalRedirectUrl = url || window.location.href

  const sourceRedirect = !!source
    ? `${originalRedirectUrl.includes('?') ? '&' : '?'}source=${source ?? ''}`
    : ''

  const actionRedirect = !!action
    ? `${
        originalRedirectUrl.includes('?') || !!sourceRedirect ? '&' : '?'
      }action=${action ?? ''}`
    : ''

  const campaignRedirect = !!action
    ? `${
        originalRedirectUrl.includes('?') || !!sourceRedirect ? '&' : '?'
      }campaign=${campaign ?? ''}`
    : ''

  if (IS_SSOV2_ENABLED) {
    const url = `${originalRedirectUrl}${actionRedirect}${campaignRedirect}`
    const finalPath = getURLwithSSOTracking(url, { source })?.href || url
    const hasJoinCommunity = finalPath?.includes('joincommunity=1')
    useSSOV2Store.getState().triggerLogin({
      returnUrl: finalPath !== window.location.href ? finalPath : '',
      source: source || '',
      legalTextType: hasJoinCommunity ? 'community' : 'default',
    })
  } else {
    const url = `${originalRedirectUrl}${sourceRedirect}${actionRedirect}${campaignRedirect}`
    const returnUrl = getURLwithSSOTracking(url, { source })?.href || url
    window.location.href = SSO_URL
      ? `${SSO_URL}?returnUrl=${encodeURIComponent(returnUrl)}`
      : '/'
  }
}

export const handleGoogleOauthRedirect = () => {
  const access_token = extractParam(window.location.hash, 'access_token')
  const state = extractParam(window.location.hash, 'state')
  if (state) {
    const stateObj = new URLSearchParams(decodeURIComponent(state))
    const redirectValue = stateObj.get('redirect')
    if (redirectValue) {
      window.location.href =
        decodeURIComponent(redirectValue) +
        (decodeURIComponent(redirectValue).includes('?') ? '&' : '?') +
        'access_token=' +
        access_token
    }
  }
}

const extractParam = (targetString: string, name: string) => {
  const targetProp = name + '='
  const targetPropIndex = targetString.indexOf(targetProp)
  if (targetPropIndex !== -1) {
    const value = targetString.slice(
      targetPropIndex + targetProp.length,
      targetPropIndex + targetString.slice(targetPropIndex).indexOf('&') ||
        undefined,
    )
    return value
  }
  return null
}

export const handleRedirection = (
  data: RedirectionType,
  query: ParsedUrlQuery,
): GetServerSidePropsResult<unknown> => {
  const { slug } = query
  const fullSlug = Array.isArray(slug) ? slug.join('/') : slug || ''
  if (data.status_code === 404) {
    return {
      notFound: true,
    }
  }
  const cleanUpRedirectUrl = cleanUnicodeUrl(data.redirect_url || '')
    .split('/')
    .filter(Boolean)
    .join('/')
  //  make sure not loop redirect
  if (
    cleanUnicodeUrl(fullSlug).toLowerCase() !== cleanUpRedirectUrl.toLowerCase()
  ) {
    let redirectUrl = `/${cleanUpRedirectUrl}/`
    if (data.source_flags?.flag_query === 'pass') {
      const newQuery = query
      delete newQuery.slug
      if (Object.keys(newQuery).length > 0) {
        redirectUrl = `${redirectUrl}?${encode(newQuery)}`
      }
    }
    return {
      redirect: {
        destination: redirectUrl,
        statusCode: data.status_code || 301,
      },
    }
  }
  return {
    notFound: true,
  }
}
